import { useState, useEffect, useRef } from 'react';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { formatTimecode } from '../utils/timecode';

function timeAgo(dateStr) {
  const diff = (Date.now() - new Date(dateStr).getTime()) / 1000;
  if (diff < 60)    return 'just now';
  if (diff < 3600)  return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

function sortTopLevel(comments, order) {
  const arr = [...comments];
  if (order === 'timecode') arr.sort((a, b) => a.timestampSeconds - b.timestampSeconds);
  else if (order === 'newest') arr.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  else arr.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
  return arr;
}

function getInitials(name) {
  if (!name) return '?';
  return name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
}

const ACTION_BTN = {
  background: 'none', border: 'none', cursor: 'pointer',
  fontSize: 11, fontFamily: 'var(--font)', padding: '2px 0',
  color: '#60a5fa',
};

// Mobile fixes for the comment composer. 16px font on the textarea kills
// iOS Safari's focus auto-zoom (the main reason commenting was unusable on
// iPhone). Below 480px the Send button stacks under the textarea so the
// composer doesn't get crushed when the column is narrow.
const CP_RESPONSIVE_CSS = `
  @media (max-width: 768px) {
    .cp-textarea { font-size: 16px !important; padding: 10px 12px !important; }
  }
  @media (max-width: 480px) {
    .cp-input-row { flex-direction: column !important; align-items: stretch !important; gap: 8px !important; }
    .cp-send { width: 100% !important; padding: 12px !important; font-size: 14px !important; }
  }
`;

export default function CommentPanel({
  videoAssetId,
  currentTimeSeconds,
  onSeekTo,
  comments,
  onCommentsChange,
  activeCommentId,
  isExternal          = false,
  guestSession        = null,
  allowComments       = true,
  onGuestLoginRequired = null,
  onCommentPost       = null,
}) {
  const { user } = useAuth();
  const [sortOrder,   setSortOrder]   = useState('timecode');
  const [commentText, setCommentText] = useState('');
  const [submitting,  setSubmitting]  = useState(false);
  const commentRefs = useRef({});
  const textareaRef = useRef(null);

  useEffect(() => {
    if (!activeCommentId) return;
    const el = commentRefs.current[activeCommentId];
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [activeCommentId]);

  const showInput    = allowComments && (!isExternal || !!guestSession);
  const showGate     = allowComments && isExternal && !guestSession;
  const showDisabled = !allowComments && isExternal;

  // Shift+C anywhere on the page (outside an existing input/textarea) jumps
  // the cursor into the comment composer so the user can keep working
  // keyboard-only: Space to play/pause, Shift+C to start typing, Enter to
  // send, Space again to play. preventDefault keeps the "C" from leaking
  // into the textarea as the focus moves.
  useEffect(() => {
    if (!showInput) return;
    function handleHotkey(e) {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      if (e.shiftKey && (e.code === 'KeyC' || e.key === 'C' || e.key === 'c')) {
        e.preventDefault();
        textareaRef.current?.focus();
      }
    }
    document.addEventListener('keydown', handleHotkey, true);
    return () => document.removeEventListener('keydown', handleHotkey, true);
  }, [showInput]);

  const topLevel = comments.filter(c => !c.parentId);
  const repliesByParent = {};
  comments.forEach(c => {
    if (c.parentId) {
      repliesByParent[c.parentId] = repliesByParent[c.parentId] || [];
      repliesByParent[c.parentId].push(c);
    }
  });

  const sorted = sortTopLevel(topLevel, sortOrder);
  const tcOrder = [...topLevel].sort((a, b) => a.timestampSeconds - b.timestampSeconds);
  const commentNumbers = Object.fromEntries(tcOrder.map((c, i) => [c.id, i + 1]));

  async function handleSubmit() {
    if (!commentText.trim() || submitting) return;
    setSubmitting(true);
    try {
      if (onCommentPost) {
        await onCommentPost({ timestampSeconds: currentTimeSeconds, text: commentText.trim() });
      } else {
        await api.post('/api/comments', {
          videoAssetId,
          timestampSeconds: currentTimeSeconds,
          text: commentText.trim(),
        });
      }
      setCommentText('');
      onCommentsChange();
      // Drop focus off the textarea/Send button so the next spacebar press
      // lands on document.body and the VideoPlayer's global keydown handler
      // toggles play instead of typing a space into the empty composer.
      if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
    } catch {
      alert('Failed to post comment.');
    } finally {
      setSubmitting(false);
    }
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSubmit(); }
  }

  async function handleResolve(comment) {
    try {
      await api.patch(`/api/comments/${comment.id}/resolve`);
      onCommentsChange();
    } catch {
      alert('Failed to update comment.');
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: 'var(--bg-card)', borderLeft: '1px solid var(--border)' }}>
      <style>{CP_RESPONSIVE_CSS}</style>

      {/* Header */}
      <div style={{ padding: '14px 16px 12px', flexShrink: 0, borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ fontWeight: 700, fontSize: 13, color: 'var(--tx-primary)' }}>
          Comments
          {topLevel.length > 0 && (
            <span style={{ fontWeight: 400, color: 'var(--tx-faint)', marginLeft: 4 }}>({topLevel.length})</span>
          )}
        </span>
        <select
          value={sortOrder}
          onChange={e => setSortOrder(e.target.value)}
          style={{ background: 'var(--bg-input)', border: '1px solid var(--border-mid)', borderRadius: 6, padding: '4px 8px', fontSize: 11, color: 'var(--tx-secondary)', fontFamily: 'var(--font)', cursor: 'pointer' }}
        >
          <option value="timecode">Timecode</option>
          <option value="newest">Newest</option>
          <option value="oldest">Oldest</option>
        </select>
      </div>

      {/* Comment list */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '4px 0' }}>
        {sorted.length === 0 ? (
          <div style={{ padding: '32px 16px', textAlign: 'center', fontSize: 12, color: 'var(--tx-faint)' }}>No comments yet.</div>
        ) : sorted.map(comment => (
          <CommentCard
            key={comment.id}
            comment={comment}
            number={commentNumbers[comment.id]}
            onSeekTo={onSeekTo}
            onResolve={handleResolve}
            showResolve={!isExternal}
            isActive={activeCommentId === comment.id}
            refCallback={el => {
              if (el) commentRefs.current[comment.id] = el;
              else delete commentRefs.current[comment.id];
            }}
            currentUserId={user?.id}
            replies={repliesByParent[comment.id] || []}
            onCommentsChange={onCommentsChange}
          />
        ))}
      </div>

      {/* New comment input */}
      {showInput && (
        <div className="cp-input-block" style={{ padding: '10px 12px 14px', flexShrink: 0, borderTop: '1px solid var(--border)', background: 'var(--bg-card)' }}>
          <div style={{ fontSize: 10, color: 'var(--tx-faint)', marginBottom: 6 }}>
            Commenting at{' '}
            <span style={{ color: '#22c55e', fontWeight: 700, fontFamily: 'monospace' }}>
              {formatTimecode(currentTimeSeconds)}
            </span>
          </div>
          <div className="cp-input-row" style={{ display: 'flex', gap: 7, alignItems: 'flex-end' }}>
            <textarea
              ref={textareaRef}
              className="cp-textarea"
              value={commentText}
              onChange={e => setCommentText(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Leave a comment… (Shift+C to focus)"
              rows={2}
              style={{ flex: 1, resize: 'none', background: 'var(--bg-input)', border: '1px solid var(--border-mid)', borderRadius: 8, padding: '8px 10px', fontSize: 12, color: 'var(--tx-primary)', fontFamily: 'var(--font)', lineHeight: 1.5, outline: 'none', transition: 'border-color 0.15s' }}
              onFocus={e => (e.target.style.borderColor = '#22c55e')}
              onBlur={e  => (e.target.style.borderColor = 'var(--border-mid)')}
            />
            <button
              className="cp-send"
              onClick={handleSubmit}
              disabled={!commentText.trim() || submitting}
              style={{ flexShrink: 0, padding: '8px 12px', borderRadius: 8, fontSize: 12, fontWeight: 600, fontFamily: 'var(--font)', border: 'none', cursor: commentText.trim() ? 'pointer' : 'default', background: commentText.trim() ? '#22c55e' : 'var(--bg-card2)', color: commentText.trim() ? '#fff' : 'var(--tx-faint)', transition: 'all 0.15s' }}
            >
              Send
            </button>
          </div>
        </div>
      )}

      {showGate && (
        <div style={{ padding: '12px', flexShrink: 0, borderTop: '1px solid var(--border)' }}>
          <button className="btn-ghost" style={{ width: '100%', fontSize: 12, padding: '10px', textAlign: 'center' }} onClick={() => onGuestLoginRequired?.()}>
            Leave a comment →
          </button>
        </div>
      )}

      {showDisabled && (
        <div style={{ padding: '14px 16px', flexShrink: 0, borderTop: '1px solid var(--border)', textAlign: 'center', fontSize: 11, color: 'var(--tx-faint)' }}>
          Comments are disabled for this video.
        </div>
      )}
    </div>
  );
}

function CommentCard({ comment, number, onSeekTo, onResolve, showResolve, isActive, refCallback, currentUserId, replies, onCommentsChange }) {
  const isResolved    = !!comment.resolvedAt;
  const commenterName = comment.commenter?.name || comment.commenterName || 'Unknown';
  const initials      = comment.commenter?.initials || getInitials(commenterName);
  const isOwn         = !!(currentUserId && comment.commenter?.id === currentUserId);

  const [editing,   setEditing]   = useState(false);
  const [editText,  setEditText]  = useState(comment.text);
  const [replying,  setReplying]  = useState(false);
  const [replyText, setReplyText] = useState('');
  const [actioning, setActioning] = useState(false);

  async function handleSaveEdit() {
    if (!editText.trim() || actioning) return;
    setActioning(true);
    try {
      await api.patch(`/api/comments/${comment.id}`, { text: editText.trim() });
      onCommentsChange();
      setEditing(false);
    } catch { alert('Failed to save edit.'); }
    finally { setActioning(false); }
  }

  async function handleDelete() {
    if (!window.confirm('Delete this comment' + (replies.length ? ' and its replies' : '') + '?')) return;
    try {
      await api.delete(`/api/comments/${comment.id}`);
      onCommentsChange();
    } catch { alert('Failed to delete comment.'); }
  }

  async function handleReply() {
    if (!replyText.trim() || actioning) return;
    setActioning(true);
    try {
      await api.post('/api/comments', {
        videoAssetId:     comment.videoAssetId,
        timestampSeconds: comment.timestampSeconds,
        text:             replyText.trim(),
        parentId:         comment.id,
      });
      onCommentsChange();
      setReplying(false);
      setReplyText('');
    } catch { alert('Failed to post reply.'); }
    finally { setActioning(false); }
  }

  const sortedReplies = [...replies].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));

  return (
    <div
      ref={refCallback}
      style={{ borderBottom: '1px solid var(--border)', background: isActive ? 'rgba(34,197,94,0.07)' : 'transparent', transition: 'background 0.25s' }}
    >
      {/* Main comment body */}
      <div
        onClick={() => !editing && onSeekTo?.(comment.timestampSeconds)}
        style={{ padding: '10px 14px', cursor: editing ? 'default' : 'pointer', opacity: isResolved ? 0.52 : 1 }}
        onMouseEnter={e => { if (!isActive && !editing) e.currentTarget.style.background = 'rgba(139,109,255,0.06)'; }}
        onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
      >
        {/* Avatar row */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 7 }}>
          <div style={{ width: 28, height: 28, borderRadius: '50%', flexShrink: 0, background: comment.isExternal ? 'rgba(255,165,0,0.18)' : 'rgba(139,109,255,0.22)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, color: comment.isExternal ? '#f59e0b' : 'var(--violet)' }}>
            {initials}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--tx-primary)', lineHeight: 1.3 }}>{commenterName}</div>
            <div style={{ fontSize: 10, color: 'var(--tx-faint)' }}>{timeAgo(comment.createdAt)}</div>
          </div>
          <span style={{ fontSize: 10, color: 'var(--tx-faint)', flexShrink: 0 }}>#{number}</span>
        </div>

        {/* Timecode badge */}
        <div style={{ marginBottom: 6 }}>
          <span style={{ display: 'inline-block', fontFamily: 'monospace', fontSize: 10, fontWeight: 700, color: '#22c55e', background: 'rgba(34,197,94,0.12)', border: '1px solid rgba(34,197,94,0.28)', borderRadius: 4, padding: '1px 6px' }}>
            {formatTimecode(comment.timestampSeconds)}
          </span>
        </div>

        {/* Text or edit form */}
        {editing ? (
          <div onClick={e => e.stopPropagation()}>
            <textarea
              value={editText}
              onChange={e => setEditText(e.target.value)}
              autoFocus
              rows={3}
              style={{ width: '100%', resize: 'none', background: 'var(--bg-input)', border: '1px solid #22c55e', borderRadius: 6, padding: '6px 8px', fontSize: 12, color: 'var(--tx-primary)', fontFamily: 'var(--font)', lineHeight: 1.5, outline: 'none', boxSizing: 'border-box' }}
              onKeyDown={e => {
                if (e.key === 'Escape') { setEditing(false); setEditText(comment.text); }
                if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSaveEdit(); }
              }}
            />
            <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', marginTop: 4 }}>
              <button onClick={() => { setEditing(false); setEditText(comment.text); }} style={{ ...ACTION_BTN }}>Cancel</button>
              <button
                onClick={handleSaveEdit}
                disabled={!editText.trim() || actioning}
                style={{ background: editText.trim() ? '#22c55e' : 'var(--bg-card2)', color: editText.trim() ? '#fff' : 'var(--tx-faint)', border: 'none', cursor: editText.trim() ? 'pointer' : 'default', borderRadius: 5, fontSize: 11, fontWeight: 600, fontFamily: 'var(--font)', padding: '3px 10px' }}
              >
                {actioning ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        ) : (
          <div style={{ fontSize: 12, color: 'var(--tx-secondary)', lineHeight: 1.55, wordBreak: 'break-word', marginBottom: 6 }}>
            {comment.text}
          </div>
        )}

        {/* Actions */}
        {!editing && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => setReplying(v => !v)} style={{ ...ACTION_BTN }}>↩ Reply</button>
              {isOwn && <>
                <button onClick={() => setEditing(true)} style={{ ...ACTION_BTN }}>Edit</button>
                <button onClick={handleDelete} style={{ ...ACTION_BTN, color: 'var(--s-incomplete-t)' }}>Delete</button>
              </>}
            </div>
            {showResolve && (
              <button
                onClick={() => onResolve(comment)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px 6px', borderRadius: 4, fontSize: 11, fontFamily: 'var(--font)', fontWeight: isResolved ? 600 : 400, color: isResolved ? '#22c55e' : 'var(--tx-faint)' }}
              >
                {isResolved ? '✓ Resolved' : '○ Resolve'}
              </button>
            )}
          </div>
        )}

        {/* Inline reply composer */}
        {replying && (
          <div style={{ marginTop: 8 }} onClick={e => e.stopPropagation()}>
            <textarea
              value={replyText}
              onChange={e => setReplyText(e.target.value)}
              autoFocus
              placeholder="Write a reply…"
              rows={2}
              style={{ width: '100%', resize: 'none', background: 'var(--bg-input)', border: '1px solid var(--border-mid)', borderRadius: 6, padding: '6px 8px', fontSize: 12, color: 'var(--tx-primary)', fontFamily: 'var(--font)', lineHeight: 1.5, outline: 'none', boxSizing: 'border-box', transition: 'border-color 0.15s' }}
              onFocus={e => (e.target.style.borderColor = 'var(--violet)')}
              onBlur={e  => (e.target.style.borderColor = 'var(--border-mid)')}
              onKeyDown={e => {
                if (e.key === 'Escape') { setReplying(false); setReplyText(''); }
                if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleReply(); }
              }}
            />
            <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', marginTop: 4 }}>
              <button onClick={() => { setReplying(false); setReplyText(''); }} style={{ ...ACTION_BTN }}>Cancel</button>
              <button
                onClick={handleReply}
                disabled={!replyText.trim() || actioning}
                style={{ background: replyText.trim() ? 'var(--violet)' : 'var(--bg-card2)', color: replyText.trim() ? '#fff' : 'var(--tx-faint)', border: 'none', cursor: replyText.trim() ? 'pointer' : 'default', borderRadius: 5, fontSize: 11, fontWeight: 600, fontFamily: 'var(--font)', padding: '3px 10px' }}
              >
                {actioning ? 'Posting…' : 'Reply'}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Threaded replies */}
      {sortedReplies.length > 0 && (
        <div style={{ background: 'rgba(0,0,0,0.18)', borderTop: '1px solid var(--border)' }}>
          {sortedReplies.map(reply => (
            <ReplyCard
              key={reply.id}
              reply={reply}
              currentUserId={currentUserId}
              onCommentsChange={onCommentsChange}
              onSeekTo={onSeekTo}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function ReplyCard({ reply, currentUserId, onCommentsChange, onSeekTo }) {
  const commenterName = reply.commenter?.name || reply.commenterName || 'Unknown';
  const initials      = reply.commenter?.initials || getInitials(commenterName);
  const isOwn         = !!(currentUserId && reply.commenter?.id === currentUserId);

  const [editing,  setEditing]  = useState(false);
  const [editText, setEditText] = useState(reply.text);
  const [actioning, setActioning] = useState(false);

  async function handleSaveEdit() {
    if (!editText.trim() || actioning) return;
    setActioning(true);
    try {
      await api.patch(`/api/comments/${reply.id}`, { text: editText.trim() });
      onCommentsChange();
      setEditing(false);
    } catch { alert('Failed to save edit.'); }
    finally { setActioning(false); }
  }

  async function handleDelete() {
    if (!window.confirm('Delete this reply?')) return;
    try {
      await api.delete(`/api/comments/${reply.id}`);
      onCommentsChange();
    } catch { alert('Failed to delete reply.'); }
  }

  return (
    <div
      onClick={() => !editing && onSeekTo?.(reply.timestampSeconds)}
      style={{ padding: '8px 14px 8px 30px', cursor: editing ? 'default' : 'pointer', borderTop: '1px solid var(--border)', position: 'relative' }}
      onMouseEnter={e => { if (!editing) e.currentTarget.style.background = 'rgba(139,109,255,0.04)'; }}
      onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
    >
      {/* Indent line */}
      <div style={{ position: 'absolute', left: 20, top: 0, bottom: 0, width: 2, background: 'rgba(139,109,255,0.2)', borderRadius: 1 }} />

      <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 5 }}>
        <div style={{ width: 22, height: 22, borderRadius: '50%', flexShrink: 0, background: reply.isExternal ? 'rgba(255,165,0,0.18)' : 'rgba(139,109,255,0.22)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 700, color: reply.isExternal ? '#f59e0b' : 'var(--violet)' }}>
          {initials}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--tx-primary)' }}>{commenterName}</span>
          <span style={{ fontSize: 10, color: 'var(--tx-faint)', marginLeft: 6 }}>{timeAgo(reply.createdAt)}</span>
        </div>
      </div>

      {editing ? (
        <div onClick={e => e.stopPropagation()}>
          <textarea
            value={editText}
            onChange={e => setEditText(e.target.value)}
            autoFocus
            rows={2}
            style={{ width: '100%', resize: 'none', background: 'var(--bg-input)', border: '1px solid var(--violet)', borderRadius: 6, padding: '5px 7px', fontSize: 11, color: 'var(--tx-primary)', fontFamily: 'var(--font)', lineHeight: 1.5, outline: 'none', boxSizing: 'border-box' }}
            onKeyDown={e => {
              if (e.key === 'Escape') { setEditing(false); setEditText(reply.text); }
              if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSaveEdit(); }
            }}
          />
          <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', marginTop: 3 }}>
            <button onClick={() => { setEditing(false); setEditText(reply.text); }} style={{ ...ACTION_BTN, fontSize: 10 }}>Cancel</button>
            <button onClick={handleSaveEdit} disabled={!editText.trim() || actioning} style={{ background: editText.trim() ? 'var(--violet)' : 'var(--bg-card2)', color: editText.trim() ? '#fff' : 'var(--tx-faint)', border: 'none', cursor: 'pointer', borderRadius: 4, fontSize: 10, fontWeight: 600, fontFamily: 'var(--font)', padding: '2px 8px' }}>
              {actioning ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      ) : (
        <>
          <div style={{ fontSize: 11, color: 'var(--tx-secondary)', lineHeight: 1.5, wordBreak: 'break-word', marginBottom: isOwn ? 4 : 0 }}>
            {reply.text}
          </div>
          {isOwn && (
            <div style={{ display: 'flex', gap: 8 }} onClick={e => e.stopPropagation()}>
              <button onClick={() => setEditing(true)} style={{ ...ACTION_BTN, fontSize: 10 }}>Edit</button>
              <button onClick={handleDelete} style={{ ...ACTION_BTN, fontSize: 10, color: 'var(--s-incomplete-t)' }}>Delete</button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
